import {
  TagByIdQuery,
  TagManyWithCountsQuery,
  useTagMergeMutation,
} from "gql/generated";
import { useMemo, useState } from "react";
import { TagGroupWithTags } from "../../../useTagManager";
import { getMultilingualString } from "shared/utils";
import { Select, useToastContext } from "@ttc3k/trekker";
import { useTranslation } from "react-i18next";
import { InternalRefetchQueriesInclude } from "@apollo/client";
import { usePostHog } from "shared/hooks";

export type UseTagMergeFlyupProps = {
  tag: TagByIdQuery["tagById"];
  allTagGroupsWithTags: TagGroupWithTags[];
  isOpen: boolean;
  onClose: () => void;
  onTagMergeCompleted: (
    mergedTag: TagManyWithCountsQuery["tagMany"][0],
  ) => void;
  refetchQueries?: InternalRefetchQueriesInclude;
};

export const useTagMergeFlyup = ({
  tag,
  allTagGroupsWithTags,
  onClose,
  onTagMergeCompleted,
  refetchQueries = [],
}: UseTagMergeFlyupProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER.FLYOUTS.TAG_DETAILS.TAG_MERGE_MODAL",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();

  // The tag that the current tag is being merged into
  const [mergeInto, setMergeInto] = useState<string | null>(null);

  // The collection of tags that the current tag can be merged into
  const collectionOfAvailableTags = useMemo(
    () =>
      Select.createCollection({
        items: allTagGroupsWithTags
          .filter(
            tagGroupWithTags =>
              tagGroupWithTags.tagGroup.type === tag?.tagGroup?.type,
          )
          .flatMap(tagGroupWithTags =>
            tagGroupWithTags.tags
              .filter(item => item._id !== tag?._id)
              .map(item => ({
                label: getMultilingualString(item.name),
                value: item._id as string,
                group: tagGroupWithTags.tagGroup.key,
                groupLabel: getMultilingualString(
                  tagGroupWithTags.tagGroup.name,
                ),
              })),
          ),
        groupBy: item => item.group,
      }),
    [allTagGroupsWithTags, tag],
  );

  const { trackPostHogException } = usePostHog();

  const [mergeTag, { loading: mergeTagLoading }] = useTagMergeMutation();
  /**
   * Merge the tag into the selected tag
   * @returns
   */
  const handleMergeTag = async () => {
    if (!mergeInto || !tag) return;

    try {
      const result = await mergeTag({
        variables: {
          tagIdToMergeAndDelete: tag?._id,
          tagIdToMergeInto: mergeInto,
        },
        ...(refetchQueries ? { refetchQueries } : {}),
      });
      if (result.errors) {
        throw new Error(result.errors?.[0]?.message);
      }
      if (result.data?.tagMerge) {
        const mergedIntoTagName =
          collectionOfAvailableTags.items.find(item => item.value === mergeInto)
            ?.label ?? "";
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("SUCCESS", {
            label: getMultilingualString(tag?.name ?? {}),
            mergedTag: mergedIntoTagName,
          }),
        });
        // Pass back the original tag because it needs to be removed from the UI
        onTagMergeCompleted(tag);
        onClose();
      }
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useTagMergeFlyup.ts",
        method: "handleMergeTag",
        message: "Error merging tag",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERRORS.TAG_MERGE"),
      });
    }
  };

  return {
    collectionOfAvailableTags,
    mergeInto,
    setMergeInto,
    mergeTagLoading,
    onMergeTag: handleMergeTag,
  };
};
